import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Loader } from '@kep-ui-kit/ui-components';

import {
  CommonLayout,
  StyledContentContainer,
  StyledText,
  Delay,
} from '@selfserve/react-ui';

import { useEmailValidation, useEmailVerifiedByHash } from '../data';
import { apiStatuses, routesPaths } from '@selfserve/react-utils';
import { useEffect } from 'react';
import { useAuth, useQueryParams } from '@selfserve/react-hooks';

/**
 * Helper function to determine if a URL is external (different domain)
 * @param url - The URL to check
 * @returns true if external, false if same domain
 */
const isExternalUrl = (url: URL): boolean => {
  return url.host !== window.location.host;
};

/**
 * Helper function to handle navigation logic for nextStepUrl
 * @param nextUrl - The URL to navigate to
 * @param navigate - React Router navigate function
 */
const handleUrlNavigation = (
  nextUrl: string,
  navigate: ReturnType<typeof useNavigate>
): void => {
  try {
    const url = new URL(nextUrl);

    if (isExternalUrl(url)) {
      // External URL - redirect to it directly
      window.location.href = nextUrl;
    } else {
      // Same domain - use React Router navigation
      const fullPath = url.pathname + url.search + url.hash;
      navigate(fullPath);
    }
  } catch {
    // If URL parsing fails, treat as relative path
    navigate(nextUrl);
  }
};

const StyledPleaseWaitText = styled(StyledText)(
  ({ theme }: { theme?: any }) => ({
    fontWeight: '600',
    marginBottom: '38px',
    maxWidth: '512px',
    fontSize: '32px',
    lineHeight: '42px',
  })
);

const StyledLoader = styled(Loader)(({ theme }: { theme?: any }) => ({
  width: '40px',
  height: '40px',
}));

const StyledLoadingScreen = styled.div(({ theme }: { theme?: any }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  background: 'white',
  zIndex: '10',
  display: 'flex',
  verticalAlign: 'middle',
  justifyContent: 'center',
  flexDirection: 'column',
  '& video': {
    height: '300px',
  },
  '& p': {
    textAlign: 'center',
    fontSize: '20px',
    lineHeight: '22px',
    fontWeight: '700',
    fontFamily: 'Lato',
  },
}));

export const PleaseWait = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const queryParams = useQueryParams();

  // extract email hash from id to get emailVerified flag
  const emailHash = id?.split('.')?.[0] || '';
  const emailVerified = useEmailVerifiedByHash(emailHash);

  const emailValidationData = useEmailValidation(id as string);
  const navigate = useNavigate();
  const { setHasAccessToken } = useAuth();

  useEffect(() => {
    // Wait until the API call is complete (not loading)
    if (emailValidationData.isLoading) {
      return;
    }

    // Check for React Query errors
    if (emailValidationData.isError) {
      navigate('../' + routesPaths.LINK_USED);
      return;
    }

    // Check for HTTP error status codes (5xx, 4xx)
    const httpStatus = emailValidationData.data?.status;
    if (httpStatus && httpStatus >= 400) {
      navigate('../' + routesPaths.LINK_USED);
      return;
    }

    // Check for specific API statuses
    if (emailValidationData.data?.status === apiStatuses.NOT_FOUND) {
      console.log('Email validation link not found');
      navigate('../' + routesPaths.LINK_USED);
      return;
    }

    // check if data is loaded
    if (!emailValidationData.data?.data) {
      return;
    }

    const responseData = emailValidationData.data.data;

    // the validation clears the access_token cookie and sets a new one only with an access_token - so the pricing page knows if there is a session
    setHasAccessToken(!!responseData.access_token);

    // if there is a nextStepUrl - use it for navigation
    if (responseData.nextStepUrl) {
      handleUrlNavigation(responseData.nextStepUrl, navigate);
      return;
    }

    // fallback - if there is a subscriptionId should navigate to confirmed page
    if (responseData.subscriptionId) {
      navigate(
        '../' +
        routesPaths.CONFIRMED.replace(
          ':subscriptionId',
          responseData.subscriptionId as string
        )
      );
      return;
    }

    // fallback - if no navigation path is found after data loads
    if (emailValidationData.isSuccess) {
      console.error('No navigation path found in response:', responseData);
      const nextStepUrl = responseData?.nextStepUrl || ('../' + routesPaths.LINK_USED);
      handleUrlNavigation(nextStepUrl, navigate);
    }
  }, [
    emailValidationData.isLoading,
    emailValidationData.isError,
    emailValidationData.isSuccess,
    emailValidationData.data?.status,
    emailValidationData.data?.data,
    navigate,
    setHasAccessToken,
  ]);

  return (
    <>
      <Helmet>
        <title>{t('titles.please-wait')}</title>
      </Helmet>
      <CommonLayout>
        {queryParams.hasOwnProperty('avatar-wizard') ? (
          <StyledLoadingScreen>
            <video autoPlay loop muted playsInline>
              <source src="assets/request_processing.mp4" type="video/mp4" />
            </video>
            <p>
              {emailVerified?.data?.data === 'false'
                ? t('common.creating-avatar-studio')
                : t('common.please-wait')}
            </p>
          </StyledLoadingScreen>
        ) : (
          <StyledContentContainer centered>
            <Delay animationDuration={0.5}>
              <StyledPleaseWaitText centered withMargin>
                {t('common.please-wait')}
              </StyledPleaseWaitText>
            </Delay>
            <StyledLoader size={'large'} />
          </StyledContentContainer>
        )}
      </CommonLayout>
    </>
  );
};
